/**
 * Inbound Webhooks Section — settings section for managing inbound
 * automation webhooks. Gated to schema admins.
 */

import { useMutation } from '@tanstack/react-query';
import { Plus } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { buildCreateWebhookAutomationNavState } from '@/lib/crm/components/badges/buildCreateWebhookAutomationNavState';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import type { CrmObjectListOptions } from '@/lib/crm/query/queries';
import { useWebhookTrafficPresence } from '@/lib/crm/record/timeline/useWebhookTrafficPresence';
import { useCrmObjectList } from '@/lib/crm/store';
import { logger } from '@/lib/logger';

import { setInboundWebhookEnabled } from './inbound-webhook-service';
import { WebhookCreateSlideout } from './webhook-create-slideout';
import { WebhookListItem } from './webhook-list-item';

const WEBHOOK_LIST_PROPERTIES = [
  'ahoy_display_name',
  'ahoy_webhook_enabled',
  'ahoy_webhook_auth_method',
  'ahoy_webhook_slug',
];
/** We only need to know whether the association exists, so no target properties are hydrated. */
const WEBHOOK_AUTOMATION_ASSOC = 'ahoy_automation_webhook_to_automation';
const INCLUDE_ASSOCIATIONS: NonNullable<
  CrmObjectListOptions['include_associations']
> = {
  [WEBHOOK_AUTOMATION_ASSOC]: { properties: [] },
};

export function InboundWebhooksSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { user } = useAuthInfo();
  const { isSchemaAdmin } = usePermissions();
  const { openSlideout } = useSlideoutStack();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const filters = useMemo(() => ({ properties: WEBHOOK_LIST_PROPERTIES }), []);
  const listOptions = useMemo(
    () => ({
      enabled: !!orgId && isSchemaAdmin,
      include_associations: INCLUDE_ASSOCIATIONS,
    }),
    [orgId, isSchemaAdmin]
  );

  const { data, isLoading, error, refetch } = useCrmObjectList(
    orgId ?? 0,
    'ahoy_automation_webhook',
    filters,
    listOptions
  );
  const webhooks = useMemo(() => data?.results ?? [], [data]);

  const navigate = useNavigate();
  // Gates the "Create automation" CTA during automation-script-authoring rollout; remove once GA (see issue #10157).
  const canCreateAutomation = useFeatureFlag('automation_script_authoring');
  const webhookIds = useMemo(() => webhooks.map(w => w.entity_id), [webhooks]);
  const { trafficIds } = useWebhookTrafficPresence(orgId ?? 0, webhookIds);
  const handleCreateAutomation = useCallback(
    (entityId: string, displayName: string) => {
      if (!orgId) {
        return;
      }
      const { to, state } = buildCreateWebhookAutomationNavState(
        orgId,
        entityId,
        displayName
      );
      navigate(to, { state });
    },
    [orgId, navigate]
  );

  const toggleMutation = useMutation({
    mutationFn: async ({
      entityId,
      enabled,
    }: {
      entityId: string;
      enabled: string;
    }) => {
      if (!orgId || !user) {
        throw new Error('Organization not available');
      }
      const webhook = webhooks.find(w => w.entity_id === entityId);
      const startSeq = webhook?.last_event_seq ?? 0;
      await setInboundWebhookEnabled({
        orgId,
        userId: user.id,
        entityId,
        enabled,
        startSeq,
      });
    },
    onError: err => {
      logger.error(
        'Failed to update webhook',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't update the webhook. Try again.");
      void refetch();
    },
    onSuccess: () => {
      void refetch();
      toast.success('Webhook updated');
    },
  });

  const handleToggle = useCallback(
    (entityId: string, enabled: string) => {
      toggleMutation.mutate({ entityId, enabled });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
    [toggleMutation.mutate]
  );

  const handleRowClick = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  const handleOpenCreate = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const handleCreated = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!isSchemaAdmin || !orgId) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        role='status'
        aria-live='polite'
        aria-label='Loading webhooks'
        className='space-y-3'
      >
        {[1, 2].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        error={error}
        label="We couldn't load your webhooks"
        onRetry={refetch}
      />
    );
  }

  return (
    <>
      <div>
        <div className='flex justify-between items-center mb-3'>
          <h3 className='text-sm font-semibold text-fg-primary'>
            Inbound webhooks
          </h3>
          <Button
            size='sm'
            color='secondary'
            iconLeading={Plus}
            onClick={handleOpenCreate}
          >
            Create webhook
          </Button>
        </div>
        {webhooks.length === 0 ? (
          <p className='text-sm text-fg-tertiary'>No inbound webhooks yet.</p>
        ) : (
          <ButtonList aria-label='Inbound webhooks'>
            {webhooks.map(w => {
              const hasTraffic = trafficIds.has(w.entity_id);
              const hasAssoc =
                w.associations?.some(
                  a => a.association_type_name === WEBHOOK_AUTOMATION_ASSOC
                ) ?? false;
              return (
                <WebhookListItem
                  key={w.entity_id}
                  webhook={w}
                  onToggle={handleToggle}
                  onRowClick={handleRowClick}
                  isToggling={toggleMutation.isPending}
                  hasReceivedTraffic={hasTraffic}
                  showCreateAutomationCta={
                    hasTraffic && canCreateAutomation && !hasAssoc
                  }
                  onCreateAutomation={handleCreateAutomation}
                />
              );
            })}
          </ButtonList>
        )}
      </div>
      <WebhookCreateSlideout
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        orgId={orgId}
        onCreated={handleCreated}
      />
    </>
  );
}
